import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { Chapter, Video } from '../lib/types';
import { useCurrentUser } from '../lib/auth';
import { useChannels } from '../lib/channels-context';
import { putWithProgress } from '../lib/upload';

const REACTIONS = [
  { key: 'like', mark: '👍' },
  { key: 'clap', mark: '👏' },
  { key: 'eye', mark: '👀' },
] as const;

const fmtTime = (s: number): string => {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};

export function VideoDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const me = useCurrentUser();
  const [video, setVideo] = useState<Video | null>(null);
  const [streamUrl, setStreamUrl] = useState<string>('');
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [reactionCounts, setReactionCounts] = useState<Record<string, number>>({});
  const [later, setLater] = useState(false);
  const [w, setW] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1440);
  const [current, setCurrent] = useState(0);
  const [resumeSec, setResumeSec] = useState<number>(0);
  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState<{
    title: string;
    description: string;
    channelId: string;
    visibility: 'company' | 'link';
  } | null>(null);
  const { channels } = useChannels();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [loadError, setLoadError] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const viewCountedRef = useRef(false);
  const resumedRef = useRef(false);
  const lastSavedRef = useRef<number>(0);

  useEffect(() => {
    const on = (): void => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  const canEdit = !!video && (me.isAdmin || (!!video.uploaderId && video.uploaderId === me.userId));

  const openEditor = (): void => {
    if (!video) return;
    setEditDraft({
      title: video.title,
      description: video.description ?? '',
      channelId: video.channelId,
      visibility: video.visibility,
    });
    setEditing(true);
  };

  const flash = (m: string): void => {
    setToast(m);
    setTimeout(() => setToast(''), 2400);
  };

  const saveEdit = async (): Promise<void> => {
    if (!id || !editDraft) return;
    setSaving(true);
    try {
      await api.updateVideo(id, editDraft);
      const { video: updated } = await api.getVideo(id);
      setVideo(updated);
      setEditing(false);
      flash('保存しました');
    } catch (e) {
      flash(`保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (): Promise<void> => {
    if (!id) return;
    if (!confirm('この動画を削除しますか？（動画本体・サムネも削除されます）')) return;
    try {
      await api.deleteVideo(id);
      nav('/');
    } catch (e) {
      flash(`削除失敗: ${(e as Error).message}`);
    }
  };

  const replaceFileInputRef = useRef<HTMLInputElement | null>(null);
  const thumbInputRef = useRef<HTMLInputElement | null>(null);
  const [replacing, setReplacing] = useState<{ progress: number } | null>(null);
  const [thumbUploading, setThumbUploading] = useState(false);

  const handleThumbFile = async (file: File): Promise<void> => {
    if (!id) return;
    if (!/^image\//.test(file.type)) {
      flash('画像ファイル（JPEG / PNG / WebP）を選んでください');
      return;
    }
    setThumbUploading(true);
    try {
      const { uploadUrl, uploadKey } = await api.requestThumbnailUpload(id, {
        contentType: file.type,
        filename: file.name,
      });
      // 進捗は短いので出さない
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      await new Promise<void>((resolve, reject) => {
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('upload failed'));
        xhr.send(file);
      });
      await api.completeThumbnailUpload(id, uploadKey);
      const { video: updated } = await api.getVideo(id);
      setVideo(updated);
      flash('サムネイルを更新しました');
    } catch (e) {
      flash(`失敗: ${(e as Error).message}`);
    } finally {
      setThumbUploading(false);
    }
  };

  const handleReplaceFile = async (file: File): Promise<void> => {
    if (!id) return;
    if (!/^video\//.test(file.type) && !/\.(mp4|mov|m4v)$/i.test(file.name)) {
      flash('動画ファイル（MP4 / MOV）を選んでください');
      return;
    }
    if (!confirm(`動画ファイルを「${file.name}」に差し替えますか？（メタ・視聴履歴は保持されます）`)) return;
    setReplacing({ progress: 0 });
    try {
      const { uploadUrl, uploadKey } = await api.requestReplaceUpload(id, {
        contentType: file.type || 'video/mp4',
        filename: file.name,
      });
      await putWithProgress(uploadUrl, file, (p) => setReplacing({ progress: p }));
      await api.completeReplace(id, uploadKey);
      const { video: updated, streamUrl: newUrl } = await api.getVideo(id);
      setVideo(updated);
      setStreamUrl(newUrl);
      flash('差し替えました。サムネは数秒後に更新されます');
    } catch (e) {
      flash(`差し替え失敗: ${(e as Error).message}`);
    } finally {
      setReplacing(null);
    }
  };

  useEffect(() => {
    if (!id) return;
    viewCountedRef.current = false;
    resumedRef.current = false;
    lastSavedRef.current = 0;
    setResumeSec(0);
    setLiked({});
    setReactionCounts({});
    setLater(false);
    setLoadError('');
    Promise.all([
      api.getVideo(id),
      api.getProgress(id).catch(() => ({ positionSec: 0 } as const)),
      api.listMyReactions(id).catch(() => [] as Array<'like' | 'clap' | 'eye'>),
      api.listLaterVideoIds().catch(() => [] as string[]),
    ])
      .then(([{ video, streamUrl, chapters, reactionCounts }, progress, myReactions, laterIds]) => {
        setVideo(video);
        setStreamUrl(streamUrl);
        setChapters(chapters);
        setReactionCounts(reactionCounts);
        setResumeSec(progress.positionSec ?? 0);
        setLiked(Object.fromEntries(myReactions.map((k) => [k, true])));
        setLater(laterIds.includes(id));
      })
      .catch((e: Error) => setLoadError(e.message));
  }, [id]);

  useEffect(() => {
    const key = (e: KeyboardEvent): void => {
      if (!videoRef.current) return;
      if (e.key === 'k' || e.key === ' ') {
        e.preventDefault();
        if (videoRef.current.paused) void videoRef.current.play();
        else videoRef.current.pause();
      }
      if (e.key === 'j') videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
      if (e.key === 'l') videoRef.current.currentTime = videoRef.current.currentTime + 10;
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);

  // ページ離脱時に最終位置を保存。keepalive を付けた fetch なら Authorization ヘッダも
  // 付けられて、タブが閉じても API Gateway (Cognito Authorizer) を突破して届く。
  useEffect(() => {
    if (!id) return;
    const finalSave = (): void => {
      const v = videoRef.current;
      if (!v) return;
      const pos = Math.floor(v.currentTime);
      if (pos <= 0) return;
      void api.saveProgress(id, pos, Math.floor(v.duration || 0), true);
    };
    window.addEventListener('beforeunload', finalSave);
    return () => {
      finalSave();
      window.removeEventListener('beforeunload', finalSave);
    };
  }, [id]);

  const onLoadedMetadata = (): void => {
    const v = videoRef.current;
    if (!v || resumedRef.current) return;
    resumedRef.current = true;
    // 5 秒手前から。最後まで見終わっている場合は最初から。
    if (resumeSec > 5 && (!v.duration || resumeSec < v.duration - 5)) {
      v.currentTime = Math.max(0, resumeSec - 5);
    }
  };

  const onPlay = (): void => {
    if (!id || viewCountedRef.current) return;
    viewCountedRef.current = true;
    void api.recordView(id);
  };

  const onTimeUpdate = (): void => {
    const v = videoRef.current;
    if (!v || !id) return;
    const pos = Math.floor(v.currentTime);
    setCurrent(pos);
    // 前進・後退どちらでも 10 秒以上ズレたら保存。単なる差ではなく絶対値。
    if (Math.abs(pos - lastSavedRef.current) >= 10) {
      lastSavedRef.current = pos;
      void api.saveProgress(id, pos, Math.floor(v.duration || 0));
    }
  };

  if (loadError) {
    return (
      <div style={{ padding: 30 }}>
        <div className="card" style={{ padding: 22, maxWidth: 520 }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
            動画を読み込めませんでした
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>{loadError}</div>
        </div>
      </div>
    );
  }
  if (!video) return <div style={{ padding: 30 }}>読み込み中…</div>;

  const stacked = w < 1150;
  const detailCols = stacked ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 320px';
  const activeChapter = chapters.findIndex((c, i) => {
    const next = chapters[i + 1];
    return current >= c.sec && (!next || current < next.sec);
  });

  return (
    <div style={{ padding: '24px 30px 60px', display: 'grid', gridTemplateColumns: detailCols, gap: 26, alignItems: 'start' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ aspectRatio: '16 / 9', minHeight: 240, borderRadius: 14, background: 'var(--ink-strong)', position: 'relative', overflow: 'hidden' }}>
          {streamUrl ? (
            <>
              <video
                ref={videoRef}
                src={streamUrl}
                controls
                onLoadedMetadata={onLoadedMetadata}
                onPlay={onPlay}
                onTimeUpdate={onTimeUpdate}
                style={{ width: '100%', height: '100%', display: 'block' }}
              />
              {resumeSec > 5 && current < resumeSec + 3 && (
                <div
                  style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    background: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    fontSize: 11.5,
                    padding: '5px 10px',
                    borderRadius: 999,
                  }}
                >
                  {fmtTime(resumeSec)} から再開
                </div>
              )}
            </>
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#fff', opacity: 0.6, fontSize: 12 }}>
              動画の準備中… (dev モックでは再生 URL がありません)
            </div>
          )}
        </div>

        <div style={{ padding: '18px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
            <h1 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 22, margin: 0, lineHeight: 1.4, flex: 1 }}>
              {video.title}
            </h1>
            {canEdit && (
              <div style={{ display: 'flex', gap: 6, flex: 'none', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button
                  onClick={openEditor}
                  style={smallEditBtn}
                >
                  編集
                </button>
                <button
                  onClick={() => thumbInputRef.current?.click()}
                  disabled={thumbUploading}
                  style={{ ...smallEditBtn, opacity: thumbUploading ? 0.6 : 1 }}
                >
                  {thumbUploading ? 'サムネ更新中…' : 'サムネ差し替え'}
                </button>
                <button
                  onClick={() => replaceFileInputRef.current?.click()}
                  disabled={!!replacing}
                  style={{ ...smallEditBtn, opacity: replacing ? 0.6 : 1 }}
                >
                  動画差し替え
                </button>
                <button
                  onClick={remove}
                  style={{ ...smallEditBtn, color: '#B23A3A' }}
                >
                  削除
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  background: video.tint,
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {video.presenterInitial}
              </div>
              <div style={{ lineHeight: 1.35 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{video.presenter}</div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-soft)' }}>
                  {video.channelName} · {video.views}回視聴
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
              {REACTIONS.map((r) => {
                const on = !!liked[r.key];
                const count = reactionCounts[r.key] ?? 0;
                return (
                  <button
                    key={r.key}
                    onClick={() => {
                      if (!id) return;
                      const next = !on;
                      setLiked((p) => ({ ...p, [r.key]: next }));
                      // 楽観的に count を +/- する。API 失敗時は再フェッチで整合を取る。
                      setReactionCounts((c) => ({ ...c, [r.key]: Math.max(0, (c[r.key] ?? 0) + (next ? 1 : -1)) }));
                      void api.toggleReaction(id, r.key, next);
                    }}
                    style={{
                      border: `1px solid ${on ? 'var(--brand)' : 'var(--border)'}`,
                      background: on ? 'var(--surface-tint)' : 'var(--surface)',
                      fontSize: 12.5,
                      fontWeight: 500,
                      padding: '7px 12px',
                      borderRadius: 999,
                      cursor: 'pointer',
                      color: 'var(--ink-mid)',
                      display: 'flex',
                      gap: 6,
                      alignItems: 'center',
                    }}
                  >
                    <span>{r.mark}</span>
                    {count > 0 && <span style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</span>}
                  </button>
                );
              })}
              <button
                onClick={() => {
                  if (!id) return;
                  const next = !later;
                  setLater(next);
                  void api.toggleLater(id, next);
                }}
                style={{
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                  fontSize: 12.5,
                  padding: '7px 12px',
                  borderRadius: 999,
                  cursor: 'pointer',
                  color: 'var(--ink-mid)',
                }}
              >
                {later ? '✓ あとで見るに追加済み' : '＋ あとで見る'}
              </button>
            </div>
          </div>
          <p style={{ fontSize: 13.5, color: 'var(--ink-mid)', lineHeight: 1.85, margin: '16px 0 0', maxWidth: '68ch' }}>
            {video.description}
          </p>
          <div style={{ display: 'flex', gap: 7, marginTop: 14, flexWrap: 'wrap' }}>
            {video.tags.map((t) => (
              <span
                key={t}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  color: 'var(--brand-tint)',
                  fontSize: 11.5,
                  padding: '5px 11px',
                  borderRadius: 999,
                }}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      {editing && editDraft && (
        <div
          onClick={() => !saving && setEditing(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,26,51,0.4)', display: 'grid', placeItems: 'center', zIndex: 30 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            style={{ width: 560, maxWidth: '92vw', padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}
          >
            <h2 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 17, margin: 0 }}>動画を編集</h2>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12.5, fontWeight: 700 }}>タイトル</span>
              <input
                className="input"
                value={editDraft.title}
                onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
              />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700 }}>チャンネル</span>
                <select
                  className="input"
                  value={editDraft.channelId}
                  onChange={(e) => setEditDraft({ ...editDraft, channelId: e.target.value })}
                >
                  {channels.map((c) => (
                    <option key={c.channelId} value={c.channelId}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700 }}>公開範囲</span>
                <select
                  className="input"
                  value={editDraft.visibility}
                  onChange={(e) => setEditDraft({ ...editDraft, visibility: e.target.value as 'company' | 'link' })}
                >
                  <option value="company">全社員に公開</option>
                  <option value="link">リンクを知っている人のみ</option>
                </select>
              </label>
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 12.5, fontWeight: 700 }}>説明</span>
              <textarea
                className="input"
                value={editDraft.description}
                onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                style={{ minHeight: 100, resize: 'vertical', lineHeight: 1.7 }}
              />
            </label>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 6 }}>
              <button
                onClick={() => setEditing(false)}
                disabled={saving}
                className="btn-ghost"
              >
                キャンセル
              </button>
              <button onClick={saveEdit} disabled={saving} className="btn" style={{ opacity: saving ? 0.6 : 1 }}>
                {saving ? '保存中…' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 26,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--ink)',
            color: '#fff',
            fontSize: 13,
            padding: '13px 22px',
            borderRadius: 999,
            boxShadow: '0 8px 24px rgba(31,42,68,0.16)',
            zIndex: 40,
          }}
        >
          {toast}
        </div>
      )}

      <input
        ref={replaceFileInputRef}
        type="file"
        accept="video/mp4,video/quicktime"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleReplaceFile(f);
          e.target.value = '';
        }}
      />
      <input
        ref={thumbInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleThumbFile(f);
          e.target.value = '';
        }}
      />

      {replacing && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 16px', zIndex: 40, boxShadow: '0 4px 12px rgba(31,42,68,0.12)' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>差し替え中 {replacing.progress}%</div>
          <div style={{ width: 220, height: 6, borderRadius: 3, background: 'var(--surface-hover)' }}>
            <div style={{ width: `${replacing.progress}%`, height: 6, borderRadius: 3, background: 'var(--brand)' }} />
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {chapters.length > 0 && (
          <div className="card" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border-soft)', fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 13.5 }}>
              チャプター
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {chapters.map((c, i) => (
                <button
                  key={c.sec}
                  onClick={() => {
                    if (videoRef.current) videoRef.current.currentTime = c.sec;
                    setCurrent(c.sec);
                  }}
                  style={{
                    display: 'flex',
                    gap: 11,
                    alignItems: 'baseline',
                    padding: '11px 16px',
                    border: 'none',
                    borderBottom: '1px solid var(--surface-hover)',
                    background: i === activeChapter ? 'var(--surface-tint)' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  <span style={{ fontSize: 11.5, color: 'var(--brand)', fontWeight: 700, fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
                    {c.time}
                  </span>
                  <span style={{ fontSize: 12.5, color: 'var(--ink-strong)', lineHeight: 1.55 }}>{c.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const smallEditBtn: React.CSSProperties = {
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  fontSize: 12,
  padding: '6px 10px',
  borderRadius: 8,
  cursor: 'pointer',
  color: 'var(--ink-mid)',
};
